<template>
  <span
    class="autopilot-orb"
    :class="{ 'autopilot-orb-active': active }"
    :style="{ width: `${size}px`, height: `${size}px` }"
    role="img"
    :aria-label="label"
    :aria-busy="active"
    data-testid="autopilot-orb">
    <canvas ref="canvasRef"></canvas>
  </span>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';

interface Props {
  size?: number;
  active?: boolean;
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 32,
  active: false,
  label: 'Autopilot',
});

type Vec3 = { x: number; y: number; z: number };
type Dot = {
  x: number;
  y: number;
  z: number;
  r: number;
  a: number;
  c: { r: number; g: number; b: number };
  glow?: number;
};

const canvasRef = ref<HTMLCanvasElement | null>(null);

let ctx: CanvasRenderingContext2D | null = null;
let rafId = 0;
let lastTs = 0;
let time = 0;
let energy = 0;
let running = false;
let dpr = 1;
let visible = true;
let reduceMotion = false;
let io: IntersectionObserver | null = null;
let motionQuery: MediaQueryList | null = null;

let shell: Vec3[] = [];
let rings: Vec3[][] = [];
let links: Array<[number, number]> = [];
let travelers: Array<{ lat: number; lonSpeed: number; phase: number; meridian: boolean }> = [];

const BLUE = { r: 0, g: 122, b: 255 };
const CYAN = { r: 125, g: 211, b: 255 };
const WHITE = { r: 240, g: 248, b: 255 };

/* --------------------------------- METHODS -------------------------------- */

function rotX(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x, y: p.y * c - p.z * s, z: p.y * s + p.z * c };
}

function rotY(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x * c + p.z * s, y: p.y, z: -p.x * s + p.z * c };
}

function rotZ(p: Vec3, a: number): Vec3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c, z: p.z };
}

function fibonacciSphere(count: number): Vec3[] {
  const pts: Vec3[] = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  const n = Math.max(count, 2);
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = golden * i;
    pts.push({ x: Math.cos(theta) * r, y, z: Math.sin(theta) * r });
  }
  return pts;
}

function makeRing(count: number, tiltX: number, tiltZ: number): Vec3[] {
  const pts: Vec3[] = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2;
    pts.push(rotZ(rotX({ x: Math.cos(a), y: 0, z: Math.sin(a) }, tiltX), tiltZ));
  }
  return pts;
}

function densityFor(size: number) {
  if (size <= 32) {
    return { shell: 44, ring: 36, ringCount: 2, travelers: 6, links: false };
  }
  if (size <= 40) {
    return { shell: 58, ring: 44, ringCount: 2, travelers: 8, links: false };
  }
  return { shell: 92, ring: 58, ringCount: 3, travelers: 12, links: true };
}

function buildGeometry(): void {
  const d = densityFor(props.size);
  shell = fibonacciSphere(d.shell);

  const tilts =
    d.ringCount === 2
      ? [
          [0.18, 0.08],
          [1.12, 0.55],
        ]
      : [
          [0.12, 0.04],
          [0.72, 0.35],
          [1.22, 0.9],
        ];
  rings = tilts.map(([tx, tz]) => makeRing(d.ring, tx, tz));

  travelers = Array.from({ length: d.travelers }, (_, i) => ({
    lat: (i / d.travelers - 0.5) * 1.35,
    lonSpeed: 0.55 + (i % 5) * 0.17,
    phase: i * 0.9,
    meridian: i % 2 === 0,
  }));

  links = [];
  if (d.links) {
    const maxPer = 2;
    const used = new Uint8Array(shell.length);
    for (let i = 0; i < shell.length; i++) {
      const candidates: Array<{ j: number; d2: number }> = [];
      for (let j = i + 1; j < shell.length; j++) {
        const dx = shell[i].x - shell[j].x;
        const dy = shell[i].y - shell[j].y;
        const dz = shell[i].z - shell[j].z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < 0.11) candidates.push({ j, d2 });
      }
      candidates.sort((a, b) => a.d2 - b.d2);
      for (const c of candidates) {
        if (used[i] >= maxPer || used[c.j] >= maxPer) continue;
        links.push([i, c.j]);
        used[i] += 1;
        used[c.j] += 1;
        if (used[i] >= maxPer) break;
      }
    }
  }
}

function resizeCanvas(): void {
  const canvas = canvasRef.value;
  if (!canvas) return;
  dpr = Math.min(window.devicePixelRatio || 1, 2);
  const px = Math.max(1, Math.round(props.size * dpr));
  if (canvas.width !== px || canvas.height !== px) {
    canvas.width = px;
    canvas.height = px;
  }
}

function rgba(r: number, g: number, b: number, a: number): string {
  return `rgba(${r | 0},${g | 0},${b | 0},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
}

function mix(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number
): { r: number; g: number; b: number } {
  const k = Math.max(0, Math.min(1, t));
  return {
    r: a.r + (b.r - a.r) * k,
    g: a.g + (b.g - a.g) * k,
    b: a.b + (b.b - a.b) * k,
  };
}

function project(p: Vec3, radius: number, cx: number, cy: number) {
  const perspective = 2.35;
  const depth = perspective / (perspective - p.z);
  return {
    x: cx + p.x * depth * radius,
    y: cy + p.y * depth * radius,
    depth,
    z: p.z,
  };
}

function drawDot(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: { r: number; g: number; b: number },
  alpha: number
): void {
  if (alpha < 0.02 || radius <= 0) return;
  context.beginPath();
  context.fillStyle = rgba(color.r, color.g, color.b, alpha);
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.fill();
}

function drawFrame(): void {
  const canvas = canvasRef.value;
  if (!canvas || !ctx) return;

  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(w, h) * 0.37;
  const e = energy;
  const yaw = time * (0.32 + e * 0.55);
  const pitch = Math.sin(time * 0.28) * (0.18 + e * 0.08);
  const flowPhase = time * (0.9 + e * 1.4);
  const scanY = Math.sin(time * (0.7 + e * 0.6)) * 0.55;

  ctx.clearRect(0, 0, w, h);

  const glowA = 0.1 + e * 0.28 + (e > 0.2 ? Math.sin(time * 3.2) * 0.06 : 0);
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.45);
  glow.addColorStop(0, rgba(CYAN.r, CYAN.g, CYAN.b, glowA));
  glow.addColorStop(0.42, rgba(BLUE.r, BLUE.g, BLUE.b, glowA * 0.4));
  glow.addColorStop(1, rgba(BLUE.r, BLUE.g, BLUE.b, 0));
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 1.45, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.strokeStyle = rgba(BLUE.r, BLUE.g, BLUE.b, 0.16 + e * 0.18);
  ctx.lineWidth = Math.max(1, dpr * 0.7);
  ctx.arc(cx, cy, radius * 1.02, 0, Math.PI * 2);
  ctx.stroke();

  const rotatedShell = shell.map((p) => rotX(rotY(p, yaw), pitch));

  if (links.length) {
    ctx.lineWidth = Math.max(0.6, dpr * 0.45);
    for (const [i, j] of links) {
      const a = project(rotatedShell[i], radius, cx, cy);
      const b = project(rotatedShell[j], radius, cx, cy);
      const z = Math.min(a.z, b.z);
      const alpha = (0.04 + e * 0.06) * (0.35 + (z + 1) * 0.4);
      if (alpha < 0.02) continue;
      ctx.strokeStyle = rgba(BLUE.r, BLUE.g, BLUE.b, alpha);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }

  const ringDots: Dot[] = [];

  rings.forEach((ring, ri) => {
    const spin = yaw * (0.7 + ri * 0.18) + ri * 0.8;
    for (const p of ring) {
      const q = rotX(rotY(p, spin), pitch + ri * 0.04);
      const pr = project(q, radius, cx, cy);
      const depth = (q.z + 1) * 0.5;
      ringDots.push({
        x: pr.x,
        y: pr.y,
        z: q.z,
        r: (0.35 + depth * 0.5) * dpr,
        a: 0.08 + depth * 0.42 + e * 0.16,
        c: mix(BLUE, CYAN, 0.25 + depth * 0.4),
      });
    }
  });

  const shellDots: Dot[] = [];

  for (let i = 0; i < rotatedShell.length; i++) {
    const q = rotatedShell[i];
    const pr = project(q, radius, cx, cy);
    const depth = (q.z + 1) * 0.5;
    const az = Math.atan2(q.z, q.x);
    const flow = Math.pow(Math.max(0, Math.cos(az - flowPhase)), 12);
    const band = Math.exp(-Math.pow((q.y - scanY) * 4.2, 2));
    const pulse = flow * (0.55 + e * 0.7) + band * (0.2 + e * 0.45);
    const color = mix(mix(BLUE, CYAN, depth * 0.4), WHITE, pulse * 0.9);
    shellDots.push({
      x: pr.x,
      y: pr.y,
      z: q.z,
      r: (0.42 + depth * 0.95 + pulse * 1.05) * dpr * (props.size >= 48 ? 1.15 : 1),
      a: 0.06 + depth * 0.7 + pulse * 0.38 + e * 0.1,
      c: color,
      glow: pulse,
    });
  }

  for (const t of travelers) {
    const u = flowPhase * t.lonSpeed + t.phase;
    const p: Vec3 = t.meridian
      ? { x: Math.cos(u) * Math.cos(t.lat), y: Math.sin(u), z: Math.cos(u) * Math.sin(t.lat) }
      : { x: Math.cos(t.lat) * Math.cos(u), y: Math.sin(t.lat), z: Math.cos(t.lat) * Math.sin(u) };
    const q = rotX(rotY(p, yaw * 0.15), pitch);
    const pr = project(q, radius, cx, cy);
    const depth = (q.z + 1) * 0.5;
    shellDots.push({
      x: pr.x,
      y: pr.y,
      z: q.z,
      r: (1.05 + depth * 0.85 + e * 0.7) * dpr,
      a: 0.28 + depth * 0.55 + e * 0.3,
      c: mix(CYAN, WHITE, 0.4 + e * 0.4),
      glow: 0.7 + e * 0.5,
    });
  }

  const all = ringDots.concat(shellDots).sort((a, b) => a.z - b.z);
  for (const dot of all) {
    if (dot.glow && dot.glow > 0.35) {
      drawDot(ctx, dot.x, dot.y, dot.r * 2.4, mix(CYAN, WHITE, 0.4), dot.a * 0.22 * dot.glow);
    }
    drawDot(ctx, dot.x, dot.y, dot.r, dot.c, dot.a);
  }
}

function tick(ts: number): void {
  if (!running) return;
  const dt = lastTs ? Math.min(0.05, (ts - lastTs) / 1000) : 0.016;
  lastTs = ts;
  const target = props.active ? 1 : 0;
  energy += (target - energy) * Math.min(1, dt * 4.2);
  if (!reduceMotion) {
    time += dt * (0.55 + energy * 0.9);
  }
  drawFrame();
  if (!reduceMotion && visible) {
    rafId = requestAnimationFrame(tick);
  } else {
    running = false;
    rafId = 0;
  }
}

function startLoop(): void {
  if (running) return;
  running = true;
  lastTs = 0;
  rafId = requestAnimationFrame(tick);
}

function stopLoop(): void {
  running = false;
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
}

function onVisibility(): void {
  visible = document.visibilityState !== 'hidden';
  if (visible && !reduceMotion) startLoop();
  else if (!visible) stopLoop();
}

function onMotionChange(): void {
  reduceMotion = !!motionQuery?.matches;
  if (reduceMotion) {
    stopLoop();
    energy = props.active ? 1 : 0;
    drawFrame();
  } else {
    startLoop();
  }
}

/* --------------------------------- LIFECYCLE HOOKS -------------------------------- */

onMounted(() => {
  const canvas = canvasRef.value;
  if (!canvas) return;
  ctx = canvas.getContext('2d');
  if (!ctx) return;

  buildGeometry();
  resizeCanvas();
  energy = props.active ? 1 : 0;

  if (typeof window.matchMedia === 'function') {
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduceMotion = motionQuery.matches;
    motionQuery.addEventListener('change', onMotionChange);
  }

  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver(
      (entries) => {
        const show = entries.some((entry) => entry.isIntersecting);
        visible = show && document.visibilityState !== 'hidden';
        if (visible && !reduceMotion) startLoop();
        else {
          stopLoop();
          if (show) drawFrame();
        }
      },
      { threshold: 0.01 }
    );
    io.observe(canvas);
  }

  document.addEventListener('visibilitychange', onVisibility);

  if (reduceMotion) {
    drawFrame();
  } else {
    startLoop();
  }
});

onUnmounted(() => {
  stopLoop();
  io?.disconnect();
  io = null;
  motionQuery?.removeEventListener('change', onMotionChange);
  document.removeEventListener('visibilitychange', onVisibility);
  ctx = null;
});

watch(
  () => props.size,
  () => {
    buildGeometry();
    resizeCanvas();
    if (reduceMotion || !running) drawFrame();
  }
);

watch(
  () => props.active,
  () => {
    if (reduceMotion) {
      energy = props.active ? 1 : 0;
      drawFrame();
    } else if (visible) {
      startLoop();
    }
  }
);
</script>

<style scoped>
.autopilot-orb {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  line-height: 0;
  pointer-events: none;
  overflow: visible;
}

.autopilot-orb canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
